import {
  CHART_FIELD_TYPES,
  type ChartFieldCandidate,
  type ChartFieldRole,
  type ChartFieldRoleFields,
  type ChartFieldRoleResolution,
  type ChartFieldRoleSelection,
  type ChartFieldRoleSources,
  type ChartFieldType,
} from '../../models/chart.types'

import type { SelectOptionMeta } from '@/shared/ui/select'

export interface NormalizedChartFieldRoles {
  roles: ChartFieldRole[]
  valid: boolean
}

const ROLE_KEY = /^[a-z][a-z0-9_]{0,63}$/

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function nonEmptyText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function integer(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null
}

function isChartFieldType(value: unknown): value is ChartFieldType {
  return (CHART_FIELD_TYPES as readonly unknown[]).includes(value)
}

function normalizeRole(value: unknown): ChartFieldRole | null {
  if (!isRecord(value)) return null
  const key = nonEmptyText(value.key)
  const label = nonEmptyText(value.label)
  const fieldType = value.field_type
  if (!key || !ROLE_KEY.test(key) || !label || !isChartFieldType(fieldType)) return null
  if (typeof value.required !== 'boolean') return null

  const multiple = value.multiple === true
  const min = multiple ? integer(value.min) : value.required ? 1 : 0
  const max = multiple ? integer(value.max) : 1
  if (min === null || max === null || min > max || (value.required && min === 0)) return null

  const rawDescription = value.description
  const description =
    rawDescription === undefined || rawDescription === null ? null : nonEmptyText(rawDescription)
  if (rawDescription !== undefined && rawDescription !== null && description === null) return null

  return {
    key,
    fieldType,
    label,
    description,
    required: value.required,
    multiple,
    min,
    max,
  }
}

/** Validate the open backend slot vocabulary at the HTTP/store boundary. One malformed entry makes
 *  the whole slot map unusable; exposing only a valid subset could change a chart's meaning. */
export function normalizeChartFieldRoles(value: unknown): NormalizedChartFieldRoles {
  if (!Array.isArray(value) || value.length === 0) return { roles: [], valid: false }

  const roles: ChartFieldRole[] = []
  const keys = new Set<string>()
  for (const entry of value) {
    const role = normalizeRole(entry)
    if (!role || keys.has(role.key)) return { roles: [], valid: false }
    keys.add(role.key)
    roles.push(role)
  }
  return { roles, valid: true }
}

function uniqueCandidates(
  values: readonly ChartFieldCandidate[] | undefined,
): ChartFieldCandidate[] {
  const seen = new Set<string>()
  return (values ?? []).flatMap((candidate) => {
    if (!candidate.value || seen.has(candidate.value)) return []
    seen.add(candidate.value)
    return [{ ...candidate }]
  })
}

function candidatesFor(
  fieldType: ChartFieldType,
  sources: ChartFieldRoleSources,
): ChartFieldCandidate[] {
  if (fieldType === 'metric') return uniqueCandidates(sources.metrics)
  if (fieldType === 'dimension') return uniqueCandidates(sources.dimensions)
  if (fieldType === 'time_dimension') return uniqueCandidates(sources.timeDimensions)
  if (fieldType === 'target') return uniqueCandidates(sources.targets)
  return uniqueCandidates(sources.values)
}

function savedValues(value: unknown): string[] {
  const values = typeof value === 'string' ? [value] : Array.isArray(value) ? value : []
  const seen = new Set<string>()
  return values.flatMap((entry) => {
    if (typeof entry !== 'string' || !entry || seen.has(entry)) return []
    seen.add(entry)
    return [entry]
  })
}

function savedRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

/** Reconcile durable values against current candidates, de-duplicate semantically identical slots,
 *  and fill missing assignments in backend role order. This makes every fallback deterministic. */
export function applyFieldRoleSelectionRules(
  roles: readonly ChartFieldRole[],
  sources: ChartFieldRoleSources,
  saved?: unknown,
): ChartFieldRoleResolution {
  const persisted = savedRecord(saved)
  const usedByType = new Map<ChartFieldType, Set<string>>()
  const slots = roles.map((role) => {
    const options = candidatesFor(role.fieldType, sources)
    const available = new Set(
      options.filter((option) => !option.disabled).map(({ value }) => value),
    )
    const used = usedByType.get(role.fieldType) ?? new Set<string>()
    usedByType.set(role.fieldType, used)

    const hasPersistedValue = Object.prototype.hasOwnProperty.call(persisted, role.key)
    const value = savedValues(persisted[role.key])
      .filter((field) => available.has(field) && !used.has(field))
      .slice(0, role.max)
    for (const field of value) used.add(field)

    const fillAll = role.multiple && !hasPersistedValue
    const desired = fillAll ? role.max : Math.max(role.min, value.length)
    for (const option of options) {
      if (value.length >= desired) break
      if (option.disabled || used.has(option.value)) continue
      value.push(option.value)
      used.add(option.value)
    }

    return {
      role,
      options,
      value,
      valid: value.length >= role.min && value.length <= role.max,
    }
  })

  return {
    slots,
    selection: Object.fromEntries(slots.map((slot) => [slot.role.key, [...slot.value]])),
    valid: slots.every((slot) => slot.valid),
  }
}

export function getSavedFieldRoleFields(value: unknown): string[] {
  const record = savedRecord(value)
  const seen = new Set<string>()
  return Object.values(record).flatMap((entry) =>
    savedValues(entry).flatMap((field) => {
      if (seen.has(field)) return []
      seen.add(field)
      return [field]
    }),
  )
}

function fieldBucket(result: ChartFieldRoleFields, fieldType: ChartFieldType): string[] {
  if (fieldType === 'dimension') return result.dimensions
  if (fieldType === 'metric') return result.metrics
  if (fieldType === 'time_dimension') return result.timeDimensions
  if (fieldType === 'target') return result.targets
  return result.values
}

/** Split a reconciled selection back into backend field buckets without relying on positional
 *  conventions such as "first metric is X". Time dimensions remain explicit for validation;
 *  report callers may also include them in their request `dimensions` array when required. */
export function getFieldRoleFields(
  roles: readonly ChartFieldRole[],
  selection: ChartFieldRoleSelection,
): ChartFieldRoleFields {
  const result: ChartFieldRoleFields = {
    dimensions: [],
    metrics: [],
    timeDimensions: [],
    targets: [],
    values: [],
  }
  const seen = new Map<ChartFieldType, Set<string>>()
  for (const role of roles) {
    const bucket = fieldBucket(result, role.fieldType)
    const used = seen.get(role.fieldType) ?? new Set<string>()
    seen.set(role.fieldType, used)
    for (const field of savedValues(selection[role.key])) {
      if (used.has(field)) continue
      used.add(field)
      bucket.push(field)
    }
  }
  return result
}

export function getFieldRoleValue(
  selection: ChartFieldRoleSelection | undefined,
  key: string,
): string[] {
  return savedValues(selection?.[key])
}

export function toChartFieldCandidates(
  fields: readonly string[],
  label: (field: string) => string = String,
  meta?: (field: string) => SelectOptionMeta | undefined,
): ChartFieldCandidate[] {
  const seen = new Set<string>()
  return fields.flatMap((field) => {
    if (!field || seen.has(field)) return []
    seen.add(field)
    const optionMeta = meta?.(field)
    return [{ value: field, label: label(field), ...(optionMeta ? { meta: optionMeta } : {}) }]
  })
}
